import React, { useState, useEffect, useContext } from "react";
import { Drawer, Switch, Divider, Avatar } from "antd";
import {
    IconUser,
    IconLayoutDashboard,
    IconPackage,
    IconWallet,
    IconMessage,
    IconLogout,
    IconHome,
    IconSun,
    IconMoon,
    IconChevronRight,
    IconTag,
    IconBuildingStore,
    IconUsers,
    IconMessage2,
    IconHierarchy2,
    IconGitBranch,
    IconCertificate,
    IconListDetails,
    IconBoxSeam,
    IconChartBar,
    IconAdjustmentsHorizontal,
    IconArrowsExchange,
    IconAlertTriangle,
    IconCurrencyDollar,
    IconMessageCircle,
    IconTruckDelivery,
    IconTicket,
    IconBell,
    IconGift,
    IconSettings,
    IconLanguage,
    IconChevronDown,
    IconCash,
    IconDatabase,
    IconTrash,
    IconArrowBackUp,
    IconShoppingCart,
    IconFileUpload,
    IconFileDownload,
    IconUserCircle,
    IconShieldLock,
    IconDiscount2,
    IconMapPin,
    IconShoppingBag,
    IconTruckReturn,
    IconCoins,
    IconShare,
    IconAddressBook,
    IconId,
    IconHeart,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import "../css/profileMenu.css";
import { jwtDecode } from "jwt-decode";
import { getImageUrl } from "../component/SafeImage";
import { ThemeContext } from "../util/ThemeContext";
import { usePreference } from "../context/PreferenceContext";

const GlobalProfileMenu = ({ handleLogOut, stats }) => {
    const navigate = useNavigate();
    const { t } = usePreference();
    const { darkMode, toggleDarkMode } = useContext(ThemeContext);

    const [userName, setUserName] = useState("");
    const [role, setRole] = useState("");
    const [drawerOpen, setDrawerOpen] = useState(false);

    useEffect(() => {
        // 1. Try to get name from stats
        if (stats && (stats.firstName || stats.lastName)) {
            const combined = `${stats.firstName || ""} ${stats.lastName || ""}`.trim();
            if (combined) {
                setUserName(combined);
                if (stats.role) setRole(stats.role.toLowerCase());
                else {
                    const roleInStorage = localStorage.getItem("role");
                    if (roleInStorage) setRole(roleInStorage.toLowerCase());
                }
                return;
            }
        }

        // 2. Try to get name from localStorage user object
        const userStr = localStorage.getItem("user");
        if (userStr) {
            try {
                const u = JSON.parse(userStr);
                const combined = `${u.firstName || ""} ${u.lastName || ""}`.trim();
                if (combined) {
                    setUserName(combined);
                    if (u.role) setRole(u.role.toLowerCase());
                    else {
                        const roleInStorage = localStorage.getItem("role");
                        if (roleInStorage) setRole(roleInStorage.toLowerCase());
                    }
                    return;
                }
            } catch (e) { }
        }

        // 3. Try to get name from localStorage customerName
        const customerName = localStorage.getItem("customerName");
        if (customerName) {
            setUserName(customerName);
            const roleInStorage = localStorage.getItem("role") || "user";
            setRole(roleInStorage.toLowerCase());
            return;
        }

        // 4. Fallback to decoding JWT token
        const sellerToken = localStorage.getItem("accessToken");
        const adminToken = localStorage.getItem("token");
        const customerToken = localStorage.getItem("customerToken");
        const roleInStorage = localStorage.getItem("role") || "user";

        try {
            const roleInStorageInner = localStorage.getItem("role");
            let token = sellerToken || adminToken || customerToken;

            if (token) {
                const decoded = jwtDecode(token);
                if (decoded.firstName || decoded.lastName) {
                    setUserName(`${decoded.firstName || ""} ${decoded.lastName || ""}`.trim());
                } else {
                    setUserName(decoded.name || decoded.fullName || decoded.email || "User");
                }

                if (roleInStorageInner) setRole(roleInStorageInner.toLowerCase());
                else if (sellerToken) setRole("seller");
                else if (adminToken) setRole("admin");
                else setRole("user");
            } else {
                setUserName("");
                setRole("");
            }
        } catch (err) {
            setUserName("User");
            setRole(roleInStorage);
        }
    }, [stats]);

    const [expandedGroups, setExpandedGroups] = useState({});

    const toggleGroup = (key) => {
        setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const getPermissions = () => {
        const permissionsString = localStorage.getItem("permissions");
        const rawPermissions = permissionsString ? JSON.parse(permissionsString) : [];
        return rawPermissions.map(p => p.toLowerCase().replace(/[\s\-_]/g, ""));
    };

    const hasAccess = (requiredPermission) => {
        if (!role) return false;
        const r = role.toLowerCase();
        if (r === 'superadmin') return true;

        const permissions = getPermissions();
        if (permissions.includes("fullaccess")) return true;

        const permissionsString = localStorage.getItem("permissions");
        const rawPermissions = permissionsString ? JSON.parse(permissionsString) : [];
        if (rawPermissions.includes("full_access") || rawPermissions.includes("FULL_ACCESS")) return true;

        if (!requiredPermission) return true;
        if (r === 'seller' && requiredPermission === 'manage_users') return true;

        const req = requiredPermission.toLowerCase().replace(/[\s\-_]/g, "");
        return permissions.includes(req);
    };

    const adminMenuItems = [
        {
            icon: <IconBuildingStore size={20} />,
            label: t('shop_management') || 'Shop Management',
            key: "manage-admin-shops",
            permission: "manage_sellers",
            children: [
                {
                    icon: <IconUsers size={18} />,
                    label: t('seller_investors') || 'Seller/Investors',
                    key: "seller-investors",
                    path: "/admin/seller-investors",
                    permission: "manage_sellers",
                },
                {
                    icon: <IconBuildingStore size={18} />,
                    label: t('shop') || 'Shop',
                    key: "shop",
                    path: "/admin/shop",
                    permission: "manage_sellers",
                },
                {
                    icon: <IconMessage2 size={18} />,
                    label: "Shop Ratings",
                    key: "shop-ratings",
                    path: "/admin/shop-ratings",
                    permission: "manage_sellers",
                },
            ].filter(item => hasAccess(item.permission)),
        },
        {
            icon: <IconPackage size={20} />,
            label: t('product_management') || 'Product Management',
            key: "manage-admin-products",
            permission: "manage_products",
            children: [
                {
                    icon: <IconBoxSeam size={18} />,
                    label: t('products') || 'Products',
                    key: "products-catalog",
                    path: "/admin/products-catalog",
                    permission: "manage_products",
                },
                {
                    icon: <IconHierarchy2 size={18} />,
                    label: t('categories') || 'Categories',
                    key: "categories",
                    path: "/admin/categories",
                    permission: "manage_categories",
                },
                {
                    icon: <IconGitBranch size={18} />,
                    label: t('sub_categories') || 'Sub Categories',
                    key: "sub-categories",
                    path: "/admin/sub-categories",
                    permission: "manage_categories",
                },
                {
                    icon: <IconCertificate size={18} />,
                    label: t('brands') || 'Brands',
                    key: "brands",
                    path: "/admin/brands",
                    permission: "manage_brands",
                },
                {
                    icon: <IconListDetails size={18} />,
                    label: t('product_options') || 'Product Options',
                    key: "product-options",
                    path: "/admin/product-options",
                    permission: "manage_products",
                },
                {
                    icon: <IconTag size={18} />,
                    label: t('product_tags') || 'Product Tags',
                    key: "products-tags",
                    path: "/admin/product-tags",
                    permission: "manage_products",
                },
                {
                    icon: <IconMessage2 size={18} />,
                    label: t('product_feedback') || 'Product Feedback',
                    key: "product-feedback",
                    path: "/admin/product-feedback",
                    permission: "manage_products",
                },
                {
                    icon: <IconChartBar size={18} />,
                    label: t('inventory_thresholds') || 'Inventory Thresholds',
                    key: "product-inventory-thresholds",
                    path: "/admin/product-inventory-thresholds",
                    permission: "manage_products",
                },
                {
                    icon: <IconAdjustmentsHorizontal size={18} />,
                    label: "Markup Rate Settings",
                    key: "product-settings",
                    path: "/admin/product-settings",
                    permission: "manage_settings",
                },
            ].filter(item => hasAccess(item.permission)),
        },
        {
            icon: <IconListDetails size={20} />,
            label: "Order Management",
            key: "manage-admin-orders",
            permission: "manage_orders",
            children: [
                {
                    icon: <IconListDetails size={18} />,
                    label: "Orders",
                    key: "admin-orders",
                    path: "/admin/orders",
                    permission: "manage_orders",
                },
                {
                    icon: <IconArrowsExchange size={18} />,
                    label: t('returns_refunds') || 'Returns/Refunds',
                    key: "admin-returns",
                    path: "/admin/returns",
                    permission: "manage_orders",
                },
                {
                    icon: <IconAlertTriangle size={18} />,
                    label: "Return Rules",
                    key: "admin-return-rules",
                    path: "/admin/return-rules",
                    permission: "manage_orders",
                },
                {
                    icon: <IconCurrencyDollar size={18} />,
                    label: "Seller Payout History",
                    key: "seller-payouts",
                    path: "/admin/seller-payouts",
                    permission: "manage_orders",
                },
            ].filter(item => hasAccess(item.permission)),
        },
        {
            icon: <IconUsers size={20} />,
            label: t('user_management') || 'User Management',
            key: "manage-admin-user",
            permission: "manage_users",
            children: [
                {
                    icon: <IconUser size={18} />,
                    label: t('users') || 'Users',
                    key: "user",
                    path: "/admin/user",
                    permission: "manage_users",
                },
                {
                    icon: <IconAdjustmentsHorizontal size={18} />,
                    label: t('seller_requests') || 'Seller Requests',
                    key: "seller-requests",
                    path: "/admin/seller-requests",
                    permission: "manage_sellers",
                },
                {
                    icon: <IconAdjustmentsHorizontal size={18} />,
                    label: t('admin_permissions') || 'Admin Permissions',
                    key: "admin-permissions",
                    path: "/admin/admin-permissions",
                },
                {
                    icon: <IconMessageCircle size={18} />,
                    label: t('messages') || 'Messages',
                    key: "admin-messages",
                    path: "/admin/messages",
                    permission: "manage_messages",
                },
            ].filter(item => {
                if (item.key === 'admin-permissions') {
                    return role === 'superadmin' || hasAccess('full_access');
                }
                return hasAccess(item.permission);
            }),
        },
        {
            icon: <IconTruckDelivery size={20} />,
            label: t('delivery_management') || 'Delivery Management',
            key: "delivery-management",
            permission: "manage_deliveries",
            children: [
                {
                    icon: <IconTruckDelivery size={18} />,
                    label: "Delivery Operations",
                    key: "admin-delivery-ops",
                    path: "/admin/delivery-ops",
                    permission: "manage_deliveries",
                },
                {
                    icon: <IconUser size={18} />,
                    label: t('delivery_staff') || 'Delivery Staff',
                    key: "admin-delivery-staff",
                    path: "/admin/delivery-staff",
                    permission: "manage_deliveries",
                },
                {
                    icon: <IconCurrencyDollar size={18} />,
                    label: "Delivery Payout History",
                    key: "delivery-payouts",
                    path: "/admin/delivery-payouts",
                    permission: "manage_deliveries",
                },
                {
                    icon: <IconAdjustmentsHorizontal size={18} />,
                    label: t('delivery_settings') || 'Delivery Settings',
                    key: "admin-delivery-settings",
                    path: "/admin/delivery-settings",
                    permission: "manage_settings",
                },
            ].filter(item => hasAccess(item.permission)),
        },
        {
            icon: <IconTicket size={20} />,
            label: t('marketing') || 'Marketing',
            key: "marketing",
            children: [
                {
                    icon: <IconTicket size={18} />,
                    label: t('coupons') || 'Coupons',
                    key: "admin-coupons",
                    path: "/admin/coupons",
                    permission: "manage_coupons",
                },
                {
                    icon: <IconAdjustmentsHorizontal size={18} />,
                    label: "Banners",
                    key: "admin-banners",
                    path: "/admin/banners",
                    permission: "manage_banners",
                },
                {
                    icon: <IconBell size={18} />,
                    label: "Offers",
                    key: "admin-offers",
                    path: "/admin/offers",
                    permission: "manage_offers",
                },
                {
                    icon: <IconGift size={18} />,
                    label: "Referral Settings",
                    key: "referral-settings",
                    path: "/admin/referral-settings",
                    permission: "manage_settings",
                },
            ].filter(item => hasAccess(item.permission)),
        },
        {
            icon: <IconSettings size={20} />,
            label: t('settings') || 'Settings',
            key: "settings-group",
            permission: "manage_settings",
            children: [
                {
                    icon: <IconAdjustmentsHorizontal size={18} />,
                    label: t('import_data') || 'Import Data',
                    key: "import-data",
                    path: "/admin/import",
                    permission: "manage_settings",
                },
                {
                    icon: <IconAdjustmentsHorizontal size={18} />,
                    label: t('export_data') || 'Export Data',
                    key: "export-data",
                    path: "/admin/export",
                    permission: "manage_settings",
                },
                {
                    icon: <IconLanguage size={18} />,
                    label: t('manage_language') || 'Manage Language',
                    key: "manage-language",
                    path: "/admin/language",
                    permission: "manage_settings",
                },
                {
                    icon: <IconCurrencyDollar size={18} />,
                    label: t('manage_currency') || 'Manage Currency',
                    key: "manage-currency",
                    path: "/admin/currency",
                    permission: "manage_settings",
                },
            ].filter(item => hasAccess(item.permission)),
        },
    ].filter(item => item.children && item.children.length > 0);

    const sellerMenuItems = [
        {
            icon: <IconBuildingStore size={20} />,
            label: t('shop') || 'Shop',
            key: "manage-seller-shop",
            children: [
                {
                    icon: <IconBuildingStore size={18} />,
                    label: t('shop_setup') || 'Shop Setup',
                    key: "shop-setup",
                    path: "/seller/shop-setup",
                },
                {
                    icon: <IconCash size={18} />,
                    label: t('products_sold') || 'Products Sold',
                    key: "product-sold",
                    path: "/seller/product-sold",
                },
                {
                    icon: <IconDatabase size={18} />,
                    label: t('inventory_update') || 'Inventory Update',
                    key: "inventory-update",
                    path: "/seller/inventory-update",
                },
            ],
        },
        {
            icon: <IconPackage size={20} />,
            label: t('products') || 'Products',
            key: "manage-seller-products",
            children: [
                {
                    icon: <IconAdjustmentsHorizontal size={18} />,
                    label: t('product_setup') || 'Product Setup',
                    key: "product-setup",
                    path: "/seller/product-setup",
                },
                {
                    icon: <IconAdjustmentsHorizontal size={18} />,
                    label: t('product_options') || 'Product Options',
                    key: "product-option",
                    path: "/seller/product-option",
                },
                {
                    icon: <IconMessage2 size={18} />,
                    label: t('product_feedback') || 'Product Feedback',
                    key: "seller-product-feedback",
                    path: "/seller/seller-product-feedback",
                },
                {
                    icon: <IconDiscount2 size={18} />,
                    label: "Deals Manager",
                    key: "deals-manager",
                    path: "/seller/deals",
                },
                {
                    icon: <IconTrash size={18} />,
                    label: "Restore Products",
                    key: "restore-products",
                    path: "/seller/product-setup?view=trash",
                },
                {
                    icon: <IconPackage size={18} />,
                    label: t('product_reports') || 'Product Reports',
                    key: "product-reports",
                    path: "/seller/reports/products",
                },
            ],
        },
        {
            icon: <IconChartBar size={20} />,
            label: t('sales') || 'Sales',
            key: "sales",
            children: [
                {
                    icon: <IconShoppingCart size={18} />,
                    label: t('orders') || 'Orders',
                    key: "orders",
                    path: "/seller/orders",
                },
                {
                    icon: <IconMessageCircle size={18} />,
                    label: "Order Issues",
                    key: "order-issues",
                    path: "/seller/order-issues",
                },
                {
                    icon: <IconArrowBackUp size={18} />,
                    label: "Return Requests",
                    key: "return-requests",
                    path: "/seller/returns",
                },
                {
                    icon: <IconCurrencyDollar size={18} />,
                    label: "Earnings & Transfers",
                    key: "seller-earnings",
                    path: "/seller/earnings",
                },
                {
                    icon: <IconChartBar size={18} />,
                    label: t('sales_reports') || 'Sales Reports',
                    key: "sales-reports",
                    path: "/seller/reports/sales",
                },
            ],
        },
        {
            icon: <IconArrowsExchange size={20} />,
            label: t('import_export') || 'Import/Export',
            key: "import-export",
            children: [
                {
                    icon: <IconFileUpload size={18} />,
                    label: t('import_data') || 'Import Data',
                    key: "import-data",
                    path: "/seller/import",
                },
                {
                    icon: <IconFileDownload size={18} />,
                    label: t('export_data') || 'Export Data',
                    key: "export-data",
                    path: "/seller/export",
                },
            ],
        },
        {
            icon: <IconUser size={20} />,
            label: t('account') || 'Account',
            key: "profile-menu",
            children: [
                {
                    icon: <IconUserCircle size={18} />,
                    label: t('personal_info') || 'Personal Info',
                    key: "profile",
                    path: "/seller/profile?tab=personal",
                },
                {
                    icon: <IconMapPin size={18} />,
                    label: t('my_addresses') || 'My Addresses',
                    key: "seller-addresses",
                    path: "/seller/profile?tab=address",
                },
                {
                    icon: <IconShieldLock size={18} />,
                    label: t('profile_settings') || 'Profile Settings',
                    key: "seller-settings",
                    path: "/seller/profile?tab=settings",
                },
            ],
        },
    ];

    const buyerMenuItems = [
        {
            icon: <IconShoppingBag size={20} />,
            label: t('orders') || 'Orders',
            key: "buyer-orders-group",
            children: [
                { key: "orders-list", icon: <IconShoppingBag size={18} />, label: t('my_orders') || 'My Orders', path: '/buyer/orders' },
                { key: "return-requests", icon: <IconTruckReturn size={18} />, label: t('return_requests') || 'Return Requests', path: '/buyer/return-requests' },
                { key: "cancellations", icon: <IconArrowBackUp size={18} />, label: t('cancellations_and_refunds') || 'Cancellations & Refunds', path: '/buyer/cancellation-requests' },
                { key: "payments", icon: <IconId size={18} />, label: t('payment_history') || 'Payment History', path: '/buyer/payments/history' },
            ],
        },
        {
            icon: <IconCoins size={20} />,
            label: t('rewards') || 'Rewards',
            key: "buyer-rewards-group",
            children: [
                { key: "points", icon: <IconCoins size={18} />, label: t('reward_points') || 'Reward Points', path: '/buyer/rewards/points' },
                { key: "share", icon: <IconShare size={18} />, label: t('share_earn') || 'Share & Earn', path: '/buyer/rewards/share' },
                { key: "offers", icon: <IconTag size={18} />, label: t('my_offers') || 'My Offers', path: '/buyer/rewards/offers' },
                { key: "credits", icon: <IconWallet size={18} />, label: t('my_credits') || 'My Credits', path: '/buyer/rewards/credits' },
            ],
        },
        {
            icon: <IconUser size={20} />,
            label: t('account') || 'Account',
            key: "buyer-account-group",
            children: [
                { key: "profile-info", icon: <IconId size={18} />, label: t('personal_info') || 'Personal Info', path: '/buyer/profile' },
                { key: "wishlist-items", icon: <IconHeart size={18} />, label: t('wishlist') || 'My Wishlist', path: '/buyer/wishlist' },
            ],
        },
        {
            icon: <IconAddressBook size={20} />,
            label: t('contact_us') || 'Contact Us',
            key: "buyer-contact",
            path: '/buyer/contact',
        },
    ];

    const getProfilePath = () => {
        if (role === "seller") return "/seller/profile";
        if (["admin", "superadmin"].includes(role)) return "/admin/profile";
        return "/buyer/profile";
    };

    const getDashboardPath = () => {
        if (role === "seller") return "/seller/dashboard";
        if (role === "pendingseller") return "/pending-seller";
        if (["admin", "superadmin"].includes(role)) return "/admin/dashboard";
        return "/buyer/dashboard";
    };

    const getMessagePath = () => {
        if (role === "seller") return "/seller/messages";
        if (["admin", "superadmin"].includes(role)) return "/admin/messages";
        return "/buyer/messages";
    };

    const go = (path) => {
        navigate(path);
        setDrawerOpen(false);
    };

    const avatarSrc = getImageUrl(stats?.profilePhoto) || "https://cdn-icons-png.flaticon.com/512/149/149071.png";

    const menuItems = [
        { icon: <IconHome size={20} />, label: t("home") || "Home", onClick: () => go("/") },
        ...(role !== "pendingseller" ? [
            { icon: <IconUser size={20} />, label: t("profile") || "Profile", onClick: () => go(getProfilePath()) },
        ] : []),
        {
            icon: <IconLayoutDashboard size={20} />,
            label: role === "pendingseller" ? (t("application_status") || "Application Status") : (t("dashboard") || "Dashboard"),
            onClick: () => go(getDashboardPath())
        },
    ];

    const textColor = darkMode ? '#e0e0e0' : '#1a1a1a';
    const textMuted = darkMode ? '#888' : '#666';
    const hoverBg = darkMode ? '#2a2a3e' : '#f5f5f5';
    const rowStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 18px',
        cursor: 'pointer',
        color: textColor,
        fontSize: 12,
        fontWeight: 500,
        transition: 'background 0.15s',
        background: 'transparent',
    };

    return (
        <>
            {/* Trigger: Avatar + Name */}
            <div
                className="global-profile-container pcu-flex pcu-items-center pcu-gap-8 pcu-cursor-pointer"
                onClick={() => setDrawerOpen(true)}
            >
                <span className="profile-name-text pcu-text-orange pcu-font-600 pcu-text-14 pcu-transition-all">
                    {userName || "User"}
                </span>
                <div className="avatar-wrapper pcu-flex pcu-items-center pcu-border-2 pcu-border-orange-20 pcu-radius-full pcu-padding-2">
                    <img
                        src={avatarSrc}
                        alt="Profile"
                        className="navbar-profile-avatar pcu-w-32 pcu-h-32 pcu-radius-full pcu-obj-cover"
                    />
                </div>
            </div>

            {/* Right-side Drawer */}
            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                placement="right"
                width={260}
                closable={false}
                styles={{ body: { padding: 0, background: darkMode ? '#1a1a2e' : '#fff' } }}
            >
                {/* Profile Header */}
                <div style={{
                    background: 'linear-gradient(160deg, #f97316 0%, #fb923c 100%)',
                    padding: '20px 18px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8,
                }}>
                    <Avatar
                        src={avatarSrc}
                        size={56}
                        icon={<IconUser size={26} />}
                        style={{ border: '2px solid rgba(255,255,255,0.7)', cursor: 'pointer', backgroundColor: '#c2640a' }}
                        onClick={() => go(getProfilePath())}
                    />
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{userName || "User"}</div>
                        <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11, marginTop: 2 }}>
                            {(() => {
                                if (!role) return '';
                                const r = role.toLowerCase();
                                const name = (userName || '').toLowerCase();

                                // Priority mappings
                                if (r === 'superadmin' || name.includes('super admin')) return 'Super Admin';
                                if (r === 'admin' || name.includes('admin')) return 'Admin';
                                if (r === 'seller') return 'Seller';
                                if (r === 'pendingseller') return 'Pending Seller';

                                // If it's just 'user' or 'buyer' but they have an admin name
                                if (name.includes('admin')) return 'Admin';

                                // If name already contains the role, hide it
                                if (name.includes(r)) return '';

                                return r.charAt(0).toUpperCase() + r.slice(1);
                            })()}
                        </div>
                    </div>
                </div>

                {/* Menu Items */}
                <div style={{ padding: '4px 0' }}>
                    {menuItems.map((item, idx) => (
                        <div
                            key={idx}
                            onClick={item.onClick}
                            style={rowStyle}
                            onMouseEnter={e => e.currentTarget.style.background = hoverBg}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                            <span style={{ color: '#f97316', display: 'flex', flexShrink: 0, transform: 'scale(0.85)' }}>{item.icon}</span>
                            <span style={{ flex: 1 }}>{item.label}</span>
                            <IconChevronRight size={14} style={{ color: textMuted, flexShrink: 0 }} />
                        </div>
                    ))}

                    {/* Admin Specific Menu Groups */}
                    {['admin', 'superadmin'].includes(role) && adminMenuItems.map((item) => {
                        const isExpanded = expandedGroups[item.key];
                        return (
                            <div key={item.key}>
                                <div
                                    style={rowStyle}
                                    onClick={() => toggleGroup(item.key)}
                                    onMouseEnter={e => e.currentTarget.style.background = hoverBg}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                >
                                    <span style={{ color: '#f97316', display: 'flex', flexShrink: 0, transform: 'scale(0.85)' }}>{item.icon}</span>
                                    <span style={{ flex: 1 }}>{item.label}</span>
                                    {isExpanded ? (
                                        <IconChevronDown size={14} style={{ color: textMuted, flexShrink: 0 }} />
                                    ) : (
                                        <IconChevronRight size={14} style={{ color: textMuted, flexShrink: 0 }} />
                                    )}
                                </div>
                                {isExpanded && (
                                    <div style={{ background: darkMode ? '#161626' : '#fafafa', borderLeft: '3px solid #f97316' }}>
                                        {item.children.map((child) => (
                                            <div
                                                key={child.key}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                    padding: '8px 18px 8px 36px',
                                                    cursor: 'pointer',
                                                    color: textColor,
                                                    fontSize: 11,
                                                    fontWeight: 500,
                                                    transition: 'background 0.15s',
                                                }}
                                                onClick={() => go(child.path)}
                                                onMouseEnter={e => e.currentTarget.style.background = hoverBg}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                            >
                                                <span style={{ color: '#f97316', display: 'flex', flexShrink: 0, transform: 'scale(0.8)' }}>{child.icon}</span>
                                                <span style={{ flex: 1 }}>{child.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Seller Specific Menu Groups */}
                    {role === 'seller' && sellerMenuItems.map((item) => {
                        const isExpanded = expandedGroups[item.key];
                        return (
                            <div key={item.key}>
                                <div
                                    style={rowStyle}
                                    onClick={() => toggleGroup(item.key)}
                                    onMouseEnter={e => e.currentTarget.style.background = hoverBg}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                >
                                    <span style={{ color: '#f97316', display: 'flex', flexShrink: 0, transform: 'scale(0.85)' }}>{item.icon}</span>
                                    <span style={{ flex: 1 }}>{item.label}</span>
                                    {isExpanded ? (
                                        <IconChevronDown size={14} style={{ color: textMuted, flexShrink: 0 }} />
                                    ) : (
                                        <IconChevronRight size={14} style={{ color: textMuted, flexShrink: 0 }} />
                                    )}
                                </div>
                                {isExpanded && (
                                    <div style={{ background: darkMode ? '#161626' : '#fafafa', borderLeft: '3px solid #f97316' }}>
                                        {item.children.map((child) => (
                                            <div
                                                key={child.key}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                    padding: '8px 18px 8px 36px',
                                                    cursor: 'pointer',
                                                    color: textColor,
                                                    fontSize: 11,
                                                    fontWeight: 500,
                                                    transition: 'background 0.15s',
                                                }}
                                                onClick={() => go(child.path)}
                                                onMouseEnter={e => e.currentTarget.style.background = hoverBg}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                            >
                                                <span style={{ color: '#f97316', display: 'flex', flexShrink: 0, transform: 'scale(0.8)' }}>{child.icon}</span>
                                                <span style={{ flex: 1 }}>{child.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Buyer Specific Menu Groups */}
                    {['user', 'buyer'].includes(role) && buyerMenuItems.map((item) => {
                        if (!item.children) {
                            return (
                                <div
                                    key={item.key}
                                    style={rowStyle}
                                    onClick={() => go(item.path)}
                                    onMouseEnter={e => e.currentTarget.style.background = hoverBg}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                >
                                    <span style={{ color: '#f97316', display: 'flex', flexShrink: 0, transform: 'scale(0.85)' }}>{item.icon}</span>
                                    <span style={{ flex: 1 }}>{item.label}</span>
                                    <IconChevronRight size={14} style={{ color: textMuted, flexShrink: 0 }} />
                                </div>
                            );
                        }

                        const isExpanded = expandedGroups[item.key];
                        return (
                            <div key={item.key}>
                                <div
                                    style={rowStyle}
                                    onClick={() => toggleGroup(item.key)}
                                    onMouseEnter={e => e.currentTarget.style.background = hoverBg}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                >
                                    <span style={{ color: '#f97316', display: 'flex', flexShrink: 0, transform: 'scale(0.85)' }}>{item.icon}</span>
                                    <span style={{ flex: 1 }}>{item.label}</span>
                                    {isExpanded ? (
                                        <IconChevronDown size={14} style={{ color: textMuted, flexShrink: 0 }} />
                                    ) : (
                                        <IconChevronRight size={14} style={{ color: textMuted, flexShrink: 0 }} />
                                    )}
                                </div>
                                {isExpanded && (
                                    <div style={{ background: darkMode ? '#161626' : '#fafafa', borderLeft: '3px solid #f97316' }}>
                                        {item.children.map((child) => (
                                            <div
                                                key={child.key}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                    padding: '8px 18px 8px 36px',
                                                    cursor: 'pointer',
                                                    color: textColor,
                                                    fontSize: 11,
                                                    fontWeight: 500,
                                                    transition: 'background 0.15s',
                                                }}
                                                onClick={() => go(child.path)}
                                                onMouseEnter={e => e.currentTarget.style.background = hoverBg}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                            >
                                                <span style={{ color: '#f97316', display: 'flex', flexShrink: 0, transform: 'scale(0.8)' }}>{child.icon}</span>
                                                <span style={{ flex: 1 }}>{child.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    <Divider style={{ margin: '2px 18px', width: 'auto', minWidth: 'auto' }} />

                    {/* Appearance toggle */}
                    <div style={{ ...rowStyle, cursor: 'default' }}>
                        <span style={{ color: '#f97316', display: 'flex', flexShrink: 0, transform: 'scale(0.85)' }}>
                            {darkMode ? <IconMoon size={20} /> : <IconSun size={20} />}
                        </span>
                        <span style={{ flex: 1 }}>{t("appearance") || "Appearance"}</span>
                        <Switch size="small" checked={darkMode} onChange={toggleDarkMode} />
                    </div>

                    <Divider style={{ margin: '2px 18px', width: 'auto', minWidth: 'auto' }} />

                    {/* Logout */}
                    <div
                        onClick={() => { handleLogOut(); setDrawerOpen(false); }}
                        style={{ ...rowStyle, color: '#ff4d4f', fontWeight: 600 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fff1f0'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                        <IconLogout size={20} style={{ flexShrink: 0, transform: 'scale(0.85)' }} />
                        <span>{t("logout") || "Logout"}</span>
                    </div>
                </div>
            </Drawer>
        </>
    );
};

export default GlobalProfileMenu;
